import { useCallback } from 'react';
import type { Note } from '../types/music';
import { StaffNote } from './StaffNote';
import { getNoteDisplayName, getClefDisplayName } from '../utils/noteMappings';
import { getAccidentalSymbol } from '../utils/keySignatures';

interface FlashcardProps {
  /** The note to display */
  note: Note | null;
  /** Whether the card is revealed (showing the answer) */
  isRevealed: boolean;
  /** Whether the last answer was correct */
  wasCorrect: boolean | null;
  /** Callback when the card is clicked */
  onClick: () => void;
  /** Whether to color-code the note */
  colorCode?: boolean;
  /** Feedback message to display */
  feedbackMessage: string | null;
  /** Optional className */
  className?: string;
}

/**
 * Flashcard component with flip animation
 *
 * Shows a musical note on the front, and the note name on the back.
 * Click to reveal, click again to go to next card.
 */
export function Flashcard({
  note,
  isRevealed,
  wasCorrect,
  onClick,
  colorCode = false,
  feedbackMessage,
  className = ''
}: FlashcardProps) {
  const handleClick = useCallback(() => {
    onClick();
  }, [onClick]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  }, [onClick]);

  if (!note) {
    return (
      <div className={`flashcard-container ${className}`}>
        <div className="flashcard flashcard-empty bg-white rounded-2xl shadow-lg p-8 flex items-center justify-center min-h-[280px]">
          <p className="text-gray-400 text-lg">Press Start to begin</p>
        </div>
      </div>
    );
  }

  return (
    <div
      // Key forces fresh mount on new card, preventing reverse-flip animation
      key={note.id}
      className={`flashcard-container perspective-1000 ${className}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={isRevealed ? 'Card revealed. Click for next card.' : 'Click to reveal the note name'}
    >
      <div
        className={`
          flashcard relative w-full min-h-[280px] transform-style-preserve-3d cursor-pointer
          ${isRevealed ? 'rotate-y-180 transition-transform duration-500' : ''}
        `}
      >
        {/* Front of card - shows the note on staff */}
        <div
          className={`
            flashcard-front absolute inset-0 backface-hidden
            bg-white rounded-2xl shadow-lg p-6
            flex flex-col items-center justify-center
            border-2 border-gray-100
            hover:shadow-xl transition-shadow
          `}
        >
          <div className="mb-4">
            <StaffNote
              note={note}
              width={280}
              height={160}
              colorCode={colorCode}
            />
          </div>
          <p className="text-gray-400 text-sm mt-2">
            Click or type the note name
          </p>
        </div>

        {/* Back of card - shows the answer */}
        <div
          className={`
            flashcard-back absolute inset-0 backface-hidden rotate-y-180
            bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl shadow-lg p-6
            flex flex-col items-center justify-center
            border-2 ${wasCorrect === true ? 'border-green-300' : wasCorrect === false ? 'border-amber-300' : 'border-blue-200'}
          `}
        >
          <div className="mb-4">
            <StaffNote
              note={note}
              width={280}
              height={160}
              colorCode={colorCode}
            />
          </div>

          <div className="text-center mt-4">
            <p className="text-3xl font-bold text-gray-800 mb-2">
              {note.pitchClass}
              <span className="text-2xl">{getAccidentalSymbol(note.accidental)}</span>
              <span className="text-xl text-gray-500 ml-1">{note.octave}</span>
            </p>
            <p className="text-gray-600 mb-1">
              {getNoteDisplayName(note)}
            </p>
            <p className="text-sm text-gray-500">
              {getClefDisplayName(note.clef)}
            </p>
          </div>

          <p className="text-gray-400 text-sm mt-4">
            Click for next card
          </p>
        </div>
      </div>

      {/* Feedback message overlay */}
      {feedbackMessage && (
        <div
          className={`
            absolute top-4 right-4 px-4 py-2 rounded-lg font-semibold text-sm
            animate-fade-in z-20
            ${feedbackMessage === 'Correct!'
              ? 'bg-green-500 text-white'
              : 'bg-amber-500 text-white'
            }
          `}
          role="alert"
          aria-live="polite"
        >
          {feedbackMessage}
        </div>
      )}
    </div>
  );
}

/**
 * Loading skeleton for the flashcard
 */
export function FlashcardSkeleton({ className = '' }: { className?: string }) {
  return (
    <div className={`flashcard-skeleton ${className}`}>
      <div className="bg-white rounded-2xl shadow-lg p-6 min-h-[280px] flex flex-col items-center justify-center animate-pulse">
        <div className="w-64 h-40 bg-gray-200 rounded-lg mb-4" />
        <div className="w-32 h-4 bg-gray-200 rounded" />
      </div>
    </div>
  );
}
